import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  FileText, AlertTriangle, Shield, Users, 
  Megaphone, BarChart3, LogOut, MessageCircle, ClipboardCheck, CheckCircle
} from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';
import useFeatureFlags from '@/hooks/useFeatureFlags';

const SupervisorHome = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const flags = useFeatureFlags();

  const tiles = [
    {
      title: t('home.safetyOverview'),
      description: t('home.analyticsDesc', 'View analytics and team stats'),
      icon: BarChart3,
      path: '/supervisor/overview',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600'
    },
    {
      title: t('home.jsaTitle'),
      description: t('home.jsaDesc'),
      icon: FileText,
      path: '/supervisor/jsas',
      color: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600'
    },
    {
      title: t('home.auditTitle'),
      description: t('home.auditDesc'),
      icon: Shield,
      path: '/supervisor/audits',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600'
    },
    {
      title: t('correctiveWorkflow.myActions'),
      description: t('correctiveWorkflow.myActionsDescription'),
      icon: CheckCircle,
      path: '/my-corrective-actions',
      color: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700'
    },
    {
      title: t('home.toolboxTitle'),
      description: t('home.toolboxDesc'),
      icon: Megaphone,
      path: '/supervisor/toolbox',
      color: 'bg-teal-500',
      hoverColor: 'hover:bg-teal-600'
    },
    {
      title: t('home.incidentTitle'),
      description: t('home.incidentDesc'),
      icon: AlertTriangle,
      path: '/supervisor/incidents',
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600'
    },
    {
      title: t('home.userManagement'),
      description: t('home.userManagementDesc'),
      icon: Users,
      path: '/supervisor/team',
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600'
    },
    {
      title: t('home.inspectionsTitle'),
      description: t('home.inspectionsDesc'),
      icon: ClipboardCheck,
      path: '/supervisor/inspections',
      color: 'bg-indigo-500',
      hoverColor: 'hover:bg-indigo-600'
    },
    {
      title: t('home.safetyAssistantTitle'),
      description: t('home.safetyAssistantDesc'),
      icon: MessageCircle,
      path: '/safety-assistant',
      color: 'bg-gradient-to-br from-blue-500 to-purple-600',
      hoverColor: 'hover:from-blue-600 hover:to-purple-700'
    },
    {
      title: t('fieldLogs.title'),
      description: t('fieldLogs.subtitle'),
      icon: FileText,
      path: '/supervisor/field-logs',
      color: 'bg-amber-500',
      hoverColor: 'hover:bg-amber-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* SafetyMind AI Branding */}
        <div className="mb-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-lg flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-xl">SM</span>
            </div>
            <div className="text-left">
              <h2 className="text-2xl font-bold">
                <span className="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">Safety</span>
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Mind</span>
                <span className="text-blue-600"> AI</span>
              </h2>
              <p className="text-sm text-gray-500 italic">Safety That Thinks</p>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">
              {t('home.welcomeSupervisor')}, {user?.name || ''}
            </h1>
            <p className="text-gray-600 mt-2">{user?.companyName || t('home.quickActions')}</p>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <Button
              onClick={onLogout}
              variant="outline"
              className="flex items-center gap-2 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
              data-testid="supervisor-home-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {t('common.logout')}
            </Button>
          </div>
        </div>

        {/* Tiles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiles
            .filter((tile) => tile.path !== '/my-corrective-actions' || flags.phase3CorrectiveActions)
            .map((tile) => (
            <Card
              key={tile.path}
              className="cursor-pointer transition-all hover:shadow-lg hover:scale-105"
              onClick={() => navigate(tile.path)}
              data-testid={tile.path === '/my-corrective-actions' ? 'my-corrective-actions-tile' : undefined}
            >
              <CardContent className="p-6">
                <div className={`w-12 h-12 ${tile.color} ${tile.hoverColor} rounded-lg flex items-center justify-center mb-4 transition-colors`}>
                  <tile.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {tile.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {tile.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SupervisorHome;
